import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { persistentWidgetService } from '../services/persistentWidgetService';

const PersistentWidgetContext = createContext();

export function PersistentWidgetProvider({ children }) {
  const { user } = useAuth();
  const [openWidgetId, setOpenWidgetId] = useState(null); // Which widget's modal is open (widgetId)
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [widgetStatuses, setWidgetStatuses] = useState({}); // { widgetId: { completed: bool, dismissed: bool } }
  const [loading, setLoading] = useState(true);
  const [isInCourse, setIsInCourse] = useState(false);
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false); // Prevent flashing on initial load
  const [reviewMode, setReviewMode] = useState(false); // Whether we're in review mode
  const [reviewCompletion, setReviewCompletion] = useState(null); // The completion being reviewed
  
  // Refs to prevent infinite loops
  const isCheckingRef = useRef(false); // Prevent concurrent checks
  const periodTimeoutRef = useRef(null); // Store timeout for cleanup
  const popupTimeoutRefs = useRef({}); // Store popup timeouts per widget for cleanup

  // Check persistent widget status when user changes
  useEffect(() => {
    if (user?.uid && !hasCheckedOnce) {
      checkPersistentWidgetStatus();
    } else if (!user?.uid) {
      setOpenWidgetId(null);
      setAvailableWidgets([]);
      setWidgetStatuses({});
      setHasCheckedOnce(false);
    }
  }, [user?.uid, hasCheckedOnce]);

  const checkPersistentWidgetStatus = async (skipAutoPopup = false) => {
    // Prevent concurrent checks
    if (isCheckingRef.current) {
      console.log('Already checking, skipping...');
      return;
    }
    
    if (!user?.uid) return;
    
    isCheckingRef.current = true;
    setLoading(true);
    
    try {
      // Get all widgets that should be shown today (including completed ones)
      const { widgets, error } = await persistentWidgetService.getAllWidgetsForUser(user.uid);
      
      if (error) {
        console.warn('Persistent widget service error:', error);
        setAvailableWidgets([]);
        setLoading(false);
        setHasCheckedOnce(true);
        isCheckingRef.current = false;
        return;
      }

      const widgetsList = widgets || [];
      setAvailableWidgets(widgetsList);

      // Check completion status for each widget
      const statuses = {};
      for (const widget of widgetsList) {
        const completed = await persistentWidgetService.hasCompletedForPeriod(user.uid, widget);
        // Use functional update to get latest dismissed status
        setWidgetStatuses(prev => {
          statuses[widget.id] = {
            completed,
            dismissed: prev[widget.id]?.dismissed || false,
          };
          return prev; // We'll update it below
        });
      }
      
      // Update all statuses at once
      setWidgetStatuses(prev => {
        const updated = { ...prev };
        Object.keys(statuses).forEach(widgetId => {
          updated[widgetId] = statuses[widgetId];
        });
        return updated;
      });

      // Auto-popup on first login (similar to daily decisions)
      // Only show first available widget that's not completed and not dismissed
      if (!skipAutoPopup && !isInCourse && !hasCheckedOnce) {
        const widgetToShow = widgetsList.find(w => {
          const status = statuses[w.id];
          return status && !status.completed && !status.dismissed;
        });

        if (widgetToShow) {
          // Clear any existing popup timeout for this widget
          if (popupTimeoutRefs.current[widgetToShow.id]) {
            clearTimeout(popupTimeoutRefs.current[widgetToShow.id]);
          }
          // Show after a longer delay to allow welcome and daily decision popups first
          popupTimeoutRefs.current[widgetToShow.id] = setTimeout(() => {
            // Double-check conditions before showing
            setWidgetStatuses(prev => {
              const widgetStatus = prev[widgetToShow.id];
              if (!isInCourse && widgetStatus && !widgetStatus.dismissed && !widgetStatus.completed) {
                setOpenWidgetId(widgetToShow.id);
              }
              return prev;
            });
          }, 18000); // 18 second delay - after welcome (2s) and daily decision (8s)
        }
      }
    } catch (error) {
      console.error('Error checking persistent widget status:', error);
    }
    
    setLoading(false);
    setHasCheckedOnce(true);
    isCheckingRef.current = false;
  };

  const completePersistentWidget = async (widgetId, widgetData = {}) => {
    if (!user?.uid) return { success: false };
    
    try {
      // Check if widget already has a completion (widgets save their own data)
      // If so, just update state. Otherwise, mark as completed.
      const widget = availableWidgets.find(w => w.id === widgetId);
      if (widget) {
        const hasCompleted = await persistentWidgetService.hasCompletedForPeriod(user.uid, widget);
        
        if (hasCompleted) {
          // Already completed, just update state
          setWidgetStatuses(prev => ({
            ...prev,
            [widgetId]: { ...prev[widgetId], completed: true }
          }));
          return { success: true };
        } else {
          // Mark as completed (widgets save their own data, so this is just for tracking)
          const result = await persistentWidgetService.markCompleted(user.uid, widgetId, widgetData);
          
          if (result.success) {
            // Update status for this widget
            setWidgetStatuses(prev => ({
              ...prev,
              [widgetId]: { ...prev[widgetId], completed: true }
            }));
            // Close modal for this widget
            if (openWidgetId === widgetId) {
              setOpenWidgetId(null);
            }
            // Refresh available widgets (skip auto-popup since user just completed)
            await checkPersistentWidgetStatus(true);
          }
          
          return result;
        }
      }
      
      return { success: false, error: 'Widget not found' };
    } catch (error) {
      console.error('Error completing persistent widget:', error);
      return { success: false, error: error.message };
    }
  };

  const closePersistentWidget = (widgetId) => {
    if (widgetId && openWidgetId === widgetId) {
      setOpenWidgetId(null);
      // Track dismissal for the specific widget to prevent auto-popup until next period
      setWidgetStatuses(prev => ({
        ...prev,
        [widgetId]: {
          ...prev[widgetId],
          dismissed: true
        }
      }));
    }
  };

  const openPersistentWidget = (widgetId) => {
    if (widgetId) {
      const widget = availableWidgets.find(w => w.id === widgetId);
      if (widget) {
        setOpenWidgetId(widgetId);
      }
    }
  };

  // Function to set course engagement status (call this when entering/exiting courses)
  const setCourseEngagement = (engaged) => {
    setIsInCourse(engaged);
  };

  // Reset dismissal state when period changes (daily, weekly, monthly)
  // We need to check all widgets, not just one
  useEffect(() => {
    // Clear any existing timeout
    if (periodTimeoutRef.current) {
      clearTimeout(periodTimeoutRef.current);
      periodTimeoutRef.current = null;
    }

    if (!user?.uid || availableWidgets.length === 0) {
      return;
    }

    const checkNewPeriod = () => {
      const now = new Date();
      // Find the earliest next period check across all widgets
      let earliestNextCheck = null;
      
      for (const widget of availableWidgets) {
        if (!widget.schedule) continue;
        
        const schedule = widget.schedule;
        let nextCheck;

        switch (schedule.type) {
          case 'daily': {
            // Check at midnight
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);
            nextCheck = tomorrow.getTime() - now.getTime();
            break;
          }
          case 'weekly': {
            // Check on the scheduled day of week
            if (schedule.dayOfWeek !== undefined) {
              const daysUntilNext = (schedule.dayOfWeek - now.getDay() + 7) % 7;
              const nextDay = new Date(now);
              nextDay.setDate(now.getDate() + (daysUntilNext === 0 ? 7 : daysUntilNext));
              nextDay.setHours(0, 0, 0, 0);
              nextCheck = nextDay.getTime() - now.getTime();
            } else {
              // Default to weekly check
              const nextWeek = new Date(now);
              nextWeek.setDate(now.getDate() + 7);
              nextWeek.setHours(0, 0, 0, 0);
              nextCheck = nextWeek.getTime() - now.getTime();
            }
            break;
          }
          case 'monthly': {
            // Check on the scheduled day of month
            if (schedule.dayOfMonth !== undefined) {
              const nextMonth = new Date(now);
              nextMonth.setMonth(now.getMonth() + 1);
              nextMonth.setDate(schedule.dayOfMonth);
              nextMonth.setHours(0, 0, 0, 0);
              nextCheck = nextMonth.getTime() - now.getTime();
            } else {
              // Default to first of next month
              const nextMonth = new Date(now);
              nextMonth.setMonth(now.getMonth() + 1, 1);
              nextMonth.setHours(0, 0, 0, 0);
              nextCheck = nextMonth.getTime() - now.getTime();
            }
            break;
          }
          default: {
            // Default to daily check
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);
            nextCheck = tomorrow.getTime() - now.getTime();
            break;
          }
        }

        if (nextCheck > 0 && nextCheck < 7 * 24 * 60 * 60 * 1000) {
          if (!earliestNextCheck || nextCheck < earliestNextCheck) {
            earliestNextCheck = nextCheck;
          }
        }
      }

      if (earliestNextCheck && earliestNextCheck > 0) {
        periodTimeoutRef.current = setTimeout(() => {
          // Reset dismissed status for all widgets when period changes
          setWidgetStatuses(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(widgetId => {
              updated[widgetId] = {
                ...updated[widgetId],
                dismissed: false
              };
            });
            return updated;
          });
          // Recheck persistent widget status for new period
          if (user?.uid) {
            checkPersistentWidgetStatus();
          }
          periodTimeoutRef.current = null;
        }, earliestNextCheck);
      }
    };
    
    checkNewPeriod();

    // Cleanup function
    return () => {
      if (periodTimeoutRef.current) {
        clearTimeout(periodTimeoutRef.current);
        periodTimeoutRef.current = null;
      }
      // Clear all popup timeouts
      Object.values(popupTimeoutRefs.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      popupTimeoutRefs.current = {};
    };
  }, [user?.uid, availableWidgets.map(w => w.id).join(',')]);

  // Helper to get status for a specific widget
  const getWidgetStatus = (widgetId) => {
    return widgetStatuses[widgetId] || { completed: false, dismissed: false };
  };

  // Get widget by ID
  const getWidgetById = (widgetId) => {
    return availableWidgets.find(w => w.id === widgetId);
  };

  // Open review mode for a widget
  const openReviewMode = async (widgetId, completionId = null) => {
    if (!user?.uid) return;
    
    try {
      const widget = getWidgetById(widgetId);
      if (!widget) {
        console.error('Widget not found for review:', widgetId);
        return;
      }

      let completion = null;
      
      if (completionId) {
        // Get specific completion
        const { completion: comp } = await persistentWidgetService.getLatestCompletionInPeriod(
          widgetId,
          user.uid,
          null // We'll get it from the completion
        );
        completion = comp;
      } else {
        // Get latest completion for current period
        const periodKey = persistentWidgetService.getPeriodKey(
          widget.schedule?.type || 'weekly',
          new Date(),
          widget.schedule
        );
        const { completion: comp } = await persistentWidgetService.getLatestCompletionInPeriod(
          widgetId,
          user.uid,
          periodKey
        );
        completion = comp;
      }

      console.log('completion', completion);
      if (completion) {
        setReviewCompletion(completion);
        setReviewMode(true);
        setOpenWidgetId(widgetId);
      } else {
        // Try to get any completion for this widget
        const { completions } = await persistentWidgetService.getWidgetCompletions(widgetId, user.uid);
        if (completions && completions.length > 0) {
          setReviewCompletion(completions[0]);
          setReviewMode(true);
          setOpenWidgetId(widgetId);
        } else {
          console.warn('No completions found for widget:', widgetId);
        }
      }
    } catch (error) {
      console.error('Error opening review mode:', error);
    }
  };

  // Close review mode
  const closeReviewMode = () => {
    setReviewMode(false);
    setReviewCompletion(null);
    setOpenWidgetId(null);
  };

  return (
    <PersistentWidgetContext.Provider value={{
      openWidgetId,
      availableWidgets,
      loading,
      isInCourse,
      widgetStatuses,
      reviewMode,
      reviewCompletion,
      getWidgetStatus,
      getWidgetById,
      completePersistentWidget,
      closePersistentWidget,
      openPersistentWidget,
      openReviewMode,
      closeReviewMode,
      checkPersistentWidgetStatus,
      setCourseEngagement,
    }}>
      {children}
    </PersistentWidgetContext.Provider>
  );
}

export function usePersistentWidget() {
  const context = useContext(PersistentWidgetContext);
  if (context === undefined) {
    throw new Error('usePersistentWidget must be used within a PersistentWidgetProvider');
  }
  return context;
}

